/**
 * Debug sidebar panel showing stream events.
 * Behaves like FilesSidebar - resizable and doesn't block chat interaction.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bug, Trash2, X, Filter, MessageSquare, Brain, Wrench, Database, Play, AlertCircle, FileIcon, LayoutDashboard, Activity } from 'lucide-react';
import { DebugEntry, ToolCallCard, AgentSpanCard } from './DebugEntry';
import type { GroupedToolCall, AgentSpan, EventTreeNode } from './DebugEntry';
import type { DebugEntry as DebugEntryType, StreamEventDebugEntry } from '../../types/chat';

type EventCategory = 'text' | 'reasoning' | 'tool' | 'data' | 'canvas' | 'file' | 'activity' | 'lifecycle' | 'error';

const CATEGORY_CONFIG: Record<EventCategory, { icon: React.ReactNode; label: string; color: string; activeColor: string }> = {
  text: { icon: <MessageSquare className="w-3 h-3" />, label: 'Text', color: 'text-sky-600', activeColor: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800' },
  reasoning: { icon: <Brain className="w-3 h-3" />, label: 'Reasoning', color: 'text-amber-600', activeColor: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  tool: { icon: <Wrench className="w-3 h-3" />, label: 'Tools', color: 'text-amber-600', activeColor: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  data: { icon: <Database className="w-3 h-3" />, label: 'Data', color: 'text-cyan-600', activeColor: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800' },
  canvas: { icon: <LayoutDashboard className="w-3 h-3" />, label: 'Canvas', color: 'text-purple-600', activeColor: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800' },
  file: { icon: <FileIcon className="w-3 h-3" />, label: 'Files', color: 'text-green-600', activeColor: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' },
  activity: { icon: <Activity className="w-3 h-3" />, label: 'Activity', color: 'text-emerald-600', activeColor: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' },
  lifecycle: { icon: <Play className="w-3 h-3" />, label: 'Lifecycle', color: 'text-gray-500', activeColor: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700' },
  error: { icon: <AlertCircle className="w-3 h-3" />, label: 'Errors', color: 'text-red-600', activeColor: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800' },
};

// Default: show tool, data, canvas, file, and error events; hide text and lifecycle
const DEFAULT_ENABLED_CATEGORIES: Set<EventCategory> = new Set(['tool', 'data', 'canvas', 'file', 'activity', 'error', 'reasoning']);

// Tool event types that should be grouped
const TOOL_EVENT_TYPES = new Set([
  'tool-input-start',
  'tool-input-available',
  'tool-output-available',
]);

/**
 * Check if a debug entry is a tool event that should be grouped.
 */
function isToolEvent(entry: DebugEntryType): boolean {
  if (entry.type !== 'stream-event') return false;
  const content = entry.content as StreamEventDebugEntry;
  return TOOL_EVENT_TYPES.has(content.eventType);
}

/**
 * Extract toolCallId from a tool event.
 */
function getToolCallId(entry: DebugEntryType): string | null {
  if (entry.type !== 'stream-event') return null;
  const content = entry.content as StreamEventDebugEntry;
  return (content.payload.toolCallId as string) || null;
}

/**
 * Group tool events by toolCallId while preserving order of other events.
 * Returns a mixed array of regular entries and grouped tool calls.
 */
function groupToolEvents(entries: DebugEntryType[]): (DebugEntryType | GroupedToolCall)[] {
  const toolCallMap = new Map<string, {
    toolName: string;
    input: Record<string, unknown> | null;
    output: unknown | null;
    state: 'running' | 'completed' | 'failed';
    rawEvents: DebugEntryType[];
    timestamp: Date;
    insertIndex: number;
  }>();

  const result: (DebugEntryType | GroupedToolCall | null)[] = [];

  for (const entry of entries) {
    if (!isToolEvent(entry)) {
      result.push(entry);
      continue;
    }

    const toolCallId = getToolCallId(entry);
    if (!toolCallId) {
      result.push(entry);
      continue;
    }

    const content = entry.content as StreamEventDebugEntry;
    const eventType = content.eventType;

    if (!toolCallMap.has(toolCallId)) {
      // First event for this tool call - create placeholder
      toolCallMap.set(toolCallId, {
        toolName: '',
        input: null,
        output: null,
        state: 'running',
        rawEvents: [],
        timestamp: entry.timestamp,
        insertIndex: result.length,
      });
      result.push(null); // Placeholder for grouped entry
    }

    const toolData = toolCallMap.get(toolCallId)!;
    toolData.rawEvents.push(entry);

    if (eventType === 'tool-input-start' || eventType === 'tool-input-available') {
      const toolName = content.payload.toolName as string;
      if (toolName) toolData.toolName = toolName;

      if (eventType === 'tool-input-available') {
        toolData.input = content.payload.input as Record<string, unknown>;
      }
    } else if (eventType === 'tool-output-available') {
      toolData.output = content.payload.output;
      // Check if output indicates an error
      const output = content.payload.output as Record<string, unknown> | null;
      if (output && typeof output === 'object' && 'error' in output) {
        toolData.state = 'failed';
      } else {
        toolData.state = 'completed';
      }
    }
  }

  // Replace placeholders with grouped tool calls
  for (const [toolCallId, toolData] of toolCallMap) {
    const grouped: GroupedToolCall = {
      type: 'grouped-tool-call',
      toolCallId,
      toolName: toolData.toolName || 'Unknown Tool',
      input: toolData.input,
      output: toolData.output,
      state: toolData.state,
      rawEvents: toolData.rawEvents,
      timestamp: toolData.timestamp,
    };
    result[toolData.insertIndex] = grouped;
  }

  return result.filter((item): item is DebugEntryType | GroupedToolCall => item !== null);
}

// ============================================================================
// Activity event helpers
// ============================================================================

interface ActivityInfo {
  activityType: string;
  source: string;
  ancestry: string[];
  message: string;
  timestamp: number;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    llmCalls: number;
    durationMs: number;
  };
  detail?: Record<string, unknown>;
}

function extractActivityInfo(entry: DebugEntryType): ActivityInfo | null {
  if (entry.type !== 'stream-event') return null;
  const content = entry.content as StreamEventDebugEntry;
  if (content.eventType !== 'data-activity') return null;

  const data = content.payload.data as Record<string, unknown> | undefined;
  if (!data || !data.activityType || !data.source) return null;

  const usage = data.usage as Record<string, unknown> | undefined;

  return {
    activityType: data.activityType as string,
    source: data.source as string,
    ancestry: (data.ancestry as string[]) || [data.source as string],
    message: (data.message as string) || '',
    timestamp: (data.timestamp as number) || entry.timestamp.getTime(),
    usage: usage
      ? {
          inputTokens: (usage.inputTokens as number) || (usage.input_tokens as number) || 0,
          outputTokens: (usage.outputTokens as number) || (usage.output_tokens as number) || 0,
          totalTokens: (usage.totalTokens as number) || (usage.total_tokens as number) || 0,
          llmCalls: (usage.llmCalls as number) || (usage.llm_calls as number) || 0,
          durationMs: (usage.durationMs as number) || (usage.duration_ms as number) || 0,
        }
      : undefined,
    detail: data.detail as Record<string, unknown> | undefined,
  };
}

/**
 * Build a hierarchical event tree from debug entries:
 * 1. Extract agent spans from activity events
 * 2. Nest child spans into parent spans
 * 3. Group remaining tool events
 * 4. Merge chronologically
 */
function buildEventTree(entries: DebugEntryType[]): EventTreeNode[] {
  // Step A: Extract agent spans from activity events
  const openSpans = new Map<string, AgentSpan>();
  const allSpans: AgentSpan[] = [];
  const consumedIds = new Set<string>();

  for (const entry of entries) {
    const info = extractActivityInfo(entry);
    if (!info) continue;

    const { activityType, source, ancestry, message, timestamp, usage, detail } = info;

    if (activityType === 'agent-start') {
      const span: AgentSpan = {
        type: 'agent-span',
        source,
        ancestry,
        depth: ancestry.length,
        startTime: new Date(timestamp),
        state: 'running',
        progressEvents: [],
        children: [],
      };
      openSpans.set(source, span);
      allSpans.push(span);
      consumedIds.add(entry.id);
    } else if (activityType === 'agent-progress') {
      const span = openSpans.get(source);
      if (span) {
        span.progressEvents.push({
          message,
          timestamp: new Date(timestamp),
          detail,
        });
      }
      consumedIds.add(entry.id);
    } else if (activityType === 'agent-complete' || activityType === 'agent-error') {
      const span = openSpans.get(source);
      if (span) {
        span.endTime = new Date(timestamp);
        span.durationMs = span.endTime.getTime() - span.startTime.getTime();
        span.state = activityType === 'agent-error' ? 'failed' : 'completed';
        if (usage) span.usage = usage;
        if (activityType === 'agent-error') {
          span.errorMessage = message || (detail?.error as string) || undefined;
        }
        openSpans.delete(source);
      }
      consumedIds.add(entry.id);
    }
  }

  // Step B: Nest child spans into parents
  const rootSpans: AgentSpan[] = [];
  for (const span of allSpans) {
    if (span.depth <= 1) {
      rootSpans.push(span);
    } else {
      // Find parent by ancestry
      const parentSource = span.ancestry[span.depth - 2];
      const parent = allSpans.find(
        (s) => s.source === parentSource && s.startTime <= span.startTime
      );
      if (parent) {
        parent.children.push(span);
      } else {
        // No parent found, treat as root
        rootSpans.push(span);
      }
    }
  }

  // Step C: Group remaining tool events from non-consumed entries
  const remaining = entries.filter((e) => !consumedIds.has(e.id));
  const groupedRemaining = groupToolEvents(remaining);

  // Step D: Merge chronologically
  type TimestampedItem = { item: EventTreeNode; time: number };
  const merged: TimestampedItem[] = [];

  for (const span of rootSpans) {
    merged.push({ item: span, time: span.startTime.getTime() });
  }
  for (const item of groupedRemaining) {
    if (item.type === 'grouped-tool-call') {
      merged.push({ item, time: item.timestamp.getTime() });
    } else {
      merged.push({ item, time: item.timestamp.getTime() });
    }
  }

  merged.sort((a, b) => a.time - b.time);
  return merged.map((m) => m.item);
}

interface DebugSidebarProps {
  isOpen: boolean;
  entries: DebugEntryType[];
  onClose: () => void;
  onClear?: () => void;
}

export function DebugSidebar({
  isOpen,
  entries,
  onClose,
  onClear,
}: DebugSidebarProps) {
  const [width, setWidth] = useState(384);
  const [isResizing, setIsResizing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [enabledCategories, setEnabledCategories] = useState<Set<EventCategory>>(
    () => new Set(DEFAULT_ENABLED_CATEGORIES)
  );
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Toggle a category filter
  const toggleCategory = useCallback((category: EventCategory) => {
    setEnabledCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Filter entries based on enabled categories
  const filteredEntries = entries.filter(entry => {
    if (entry.type !== 'stream-event') {
      return true; // Show legacy entry types
    }
    const content = entry.content as StreamEventDebugEntry;
    const category = content.category || 'lifecycle';
    return enabledCategories.has(category);
  });

  // Count entries by category for the filter UI
  const categoryCounts = entries.reduce((acc, entry) => {
    if (entry.type === 'stream-event') {
      const content = entry.content as StreamEventDebugEntry;
      const category = content.category || 'lifecycle';
      acc[category] = (acc[category] || 0) + 1;
    }
    return acc;
  }, {} as Record<EventCategory, number>);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, autoScroll]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  }, []);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(280, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative"
      style={{ width: `${width}px` }}
      data-testid="debug-sidebar"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-sky-400 transition-colors z-10"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#38bdf8' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Bug size={18} className="text-emerald-600" />
          <span className="font-medium text-gray-900 dark:text-white">Debug</span>
          {entries.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
              {filteredEntries.length}/{entries.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-1.5 rounded transition-colors ${
              showFilters
                ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Filter events"
          >
            <Filter size={16} />
          </button>
          {onClear && entries.length > 0 && (
            <button
              onClick={onClear}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title="Clear all"
            >
              <Trash2 size={16} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
            title="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex-shrink-0">
          <div className="flex flex-wrap gap-1">
            {(Object.keys(CATEGORY_CONFIG) as EventCategory[]).map(category => {
              const config = CATEGORY_CONFIG[category];
              const count = categoryCounts[category] || 0;
              const enabled = enabledCategories.has(category);
              return (
                <button
                  key={category}
                  onClick={() => toggleCategory(category)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs transition-colors ${
                    enabled
                      ? config.activeColor
                      : 'text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  title={`${enabled ? 'Hide' : 'Show'} ${config.label} events`}
                >
                  {config.icon}
                  <span>{config.label}</span>
                  {count > 0 && (
                    <span className="text-gray-400 dark:text-gray-500 ml-0.5">({count})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-3 bg-gray-50/50 dark:bg-gray-800/20"
        onScroll={handleScroll}
      >
        {filteredEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bug className="w-10 h-10 text-gray-300 dark:text-gray-700 mb-3" />
            {entries.length === 0 ? (
              <>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No debug events yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Events will appear here during chat</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">All events filtered</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {entries.length} events hidden. Adjust filters above.
                </p>
              </>
            )}
          </div>
        ) : (
          buildEventTree(filteredEntries).map((node) => {
            if (node.type === 'agent-span') {
              return (
                <AgentSpanCard
                  key={`span-${node.source}-${node.startTime.getTime()}`}
                  span={node}
                />
              );
            }
            if (node.type === 'grouped-tool-call') {
              return <ToolCallCard key={node.toolCallId} toolCall={node} />;
            }
            return <DebugEntry key={node.id} entry={node} />;
          })
        )}
      </div>

      {/* Auto-scroll indicator */}
      {!autoScroll && filteredEntries.length > 0 && (
        <button
          onClick={() => {
            setAutoScroll(true);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }}
          className="absolute bottom-4 right-4 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-full shadow-lg transition-colors"
        >
          Scroll to bottom
        </button>
      )}
    </div>
  );
}

export default DebugSidebar;
